/**
 * Hook for reading feature flags from environment variables or PostHog.
 *
 * Supports two backends:
 * - Vite env vars (VITE_*) — build-time flags, read from import.meta.env
 * - PostHog — runtime flags evaluated client-side via posthog-js
 */

import { useMemo, useState, useEffect, useCallback } from 'react'
import posthog from 'posthog-js'
import { POSTHOG_FLAG_DEFAULTS as GENERATED_FLAG_DEFAULTS } from '@/generated/flagDefaults.generated'

/**
 * Known environment-based feature flags in the application
 */
export type FeatureFlagName =
  | 'VITE_USE_VERCEL_AI_CHAT'
  | 'VITE_UIV2'
  // Local-dev escape hatch for the help agent: PostHog is disabled in dev
  // (no VITE_POSTHOG_KEY), so the `help_agent_enabled` PostHog flag can
  // never resolve locally. Set in frontend/web/.env.local alongside the
  // backend's FEATURE_FLAG_OVERRIDES env.
  | 'VITE_HELP_AGENT_ENABLED'

/**
 * Known PostHog-managed feature flags
 */
export type ApiFeatureFlagName =
  | 'web_search_enabled'
  | 'expert_knowledge_enabled'
  | 'restrict_upload_to_internal_users'
  | 'chat_token_streaming_enabled'

/**
 * Defaults for PostHog-managed flags (used before PostHog loads or on error).
 */
const POSTHOG_FLAG_DEFAULTS: Readonly<Record<string, boolean>> = GENERATED_FLAG_DEFAULTS

/**
 * Check if PostHog client is initialized.
 * PostHog is disabled in dev mode and when VITE_POSTHOG_KEY is not set
 * (see posthog-config.ts). Calling methods on an uninitialized client throws.
 */
function isPostHogReady(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- posthog-js doesn't expose __loaded in its type defs
    return !!(posthog as Record<string, any>).__loaded
  } catch {
    return false
  }
}

/**
 * Read a feature flag from environment variables
 */
export function useFeatureFlag(flagName: FeatureFlagName): boolean {
  return useMemo(() => {
    const value = import.meta.env[flagName]

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1'
    }

    if (typeof value === 'boolean') {
      return value
    }

    return false
  }, [flagName])
}

/**
 * Check if running in development mode
 */
export function useIsDevelopment(): boolean {
  return import.meta.env.DEV === true
}

/**
 * Check if Vercel AI Chat feature is enabled
 */
export function useVercelAIChat(): boolean {
  return useFeatureFlag('VITE_USE_VERCEL_AI_CHAT')
}

/**
 * Hook for reading a PostHog-managed feature flag, evaluated client-side.
 * Falls back to POSTHOG_FLAG_DEFAULTS (then `defaultValue`) until PostHog
 * has loaded flags, or when PostHog is not initialized.
 */
export function useApiFeatureFlag(
  flagName: ApiFeatureFlagName | string,
  defaultValue: boolean = false
): { enabled: boolean; loading: boolean; source: 'posthog' } {
  const phDefault = POSTHOG_FLAG_DEFAULTS[flagName] ?? defaultValue

  const [phEnabled, setPhEnabled] = useState<boolean | undefined>(() => {
    if (!isPostHogReady()) return undefined
    const value = posthog.isFeatureEnabled(flagName)
    return value === undefined ? undefined : value
  })

  const handleFlagsLoaded = useCallback(() => {
    if (!isPostHogReady()) return
    const value = posthog.isFeatureEnabled(flagName)
    setPhEnabled(value === undefined ? undefined : value)
  }, [flagName])

  useEffect(() => {
    if (!isPostHogReady()) return
    const unsubscribe = posthog.onFeatureFlags(handleFlagsLoaded)
    handleFlagsLoaded()
    return unsubscribe
  }, [handleFlagsLoaded])

  return {
    enabled: phEnabled ?? phDefault,
    loading: phEnabled === undefined,
    source: 'posthog',
  }
}

/**
 * Get a feature flag value synchronously (not a hook)
 * Use this in non-React contexts
 */
export function getFeatureFlagValue(flagName: FeatureFlagName): boolean {
  const value = import.meta.env[flagName]

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1'
  }

  if (typeof value === 'boolean') {
    return value
  }

  return false
}

export default useFeatureFlag
